import * as i18nData from '@mc-alberta/i18n-data';
import load from 'load-script';
import VueI18n from 'vue-i18n';
import { lumber } from '@mc-alberta/lumber';
import 'core-js/modules/es.object.define-property.js';
import VeeValidate, { Validator } from '@mc-alberta/vee-validate-legacy';
import nuDetect from '@mc-alberta/nudetect';

const ERRORS = {
    ONE_TRUST_LOCALE_ERROR: 'ONE_TRUST_LOCALE_ERROR',
    ONE_TRUST_PARAMETER_ERROR: 'ONE_TRUST_PARAMETER_ERROR',
    ONE_TRUST_SCRIPT_ERROR: 'ONE_TRUST_SCRIPT_ERROR'
};

var t=0;function e(e){return "__private_"+t+++"_"+e}var r=/*#__PURE__*/e("checkSupport"),o=/*#__PURE__*/function(){function t(t){Object.defineProperty(this,r,{value:n}),this.type=t,this._supported=void 0;}var e,o,i=t.prototype;return i.getItem=function(t){return this.supported?window[this.type].getItem(t):null},i.key=function(t){return this.supported?window[this.type].key(t):null},i.setItem=function(t,e){this.supported&&window[this.type].setItem(t,e);},i.removeItem=function(t){this.supported&&window[this.type].removeItem(t);},i.clear=function(){this.supported&&window[this.type].clear();},e=t,(o=[{key:"supported",get:function(){return void 0!==this._supported||(this._supported=function(t,e){if(!Object.prototype.hasOwnProperty.call(t,r))throw new TypeError("attempted to use private field on non-instance");return t}(this)[r]()),this._supported}}])&&function(t,e){for(var r=0;r<e.length;r++){var o=e[r];o.enumerable=o.enumerable||!1,o.configurable=!0,"value"in o&&(o.writable=!0),Object.defineProperty(t,o.key,o);}}(e.prototype,o),Object.defineProperty(e,"prototype",{writable:!1}),t}();function n(){var t=!0;try{window[this.type].getItem("a");}catch(e){console.warn(this.type+" is not available"),t=!1;}return t}var i=new o("localStorage");new o("sessionStorage");

/**
 * @fileoverview The purpose of this abstraction is to provide a unified way to log to console and lumber
 * with the same format. That way if we want to change the way the errors look, or if we want to
 * hide them behind a Storage key or remove them, etc. we only need to do it one place.
 *
 * It's arguably fine to log full data that's available on the client-side as dedicated attackers
 * could always easily get that information themselves. However, we can enact some level of control
 * as to where we show the logs by default.
 *
 * Lumber logs should always be on production by default in all scenarios to help with debugging.
 */
const localStorageKey = 'macs_verbose_logs';
const CustomLogger = {
    /**
     * Log an error to the console for lower environments only.
     * Also logs to Lumber if `allowLumber` is true.
     *
     * @example
     * CustomLogger.error({ event: 'API_ERROR', error })
     *
     * // To add context to the error:
     * CustomLogger.error({
     *   event: 'API_ERROR',
     *   error: new Error('Some additional error context', { cause: error })
     * })
     */
    error({ details, event, error, allowLumber = true }) {
        if (!event) {
            console.error('Must provide `event` property to logger.');
            return;
        }
        const additionalDetails = getDetailsAsObject(details);
        if (allowLumber) {
            lumber.error({
                event,
                details: {
                    browser: navigator?.userAgent,
                    cause: error instanceof Error ? error?.cause : undefined,
                    message: error?.toString(),
                    ...additionalDetails
                }
            });
        }
        logToConsole();
        function logToConsole() {
            if (error && !(error instanceof Error)) {
                console.error(`[${event}] Malformed error. Must be instanceof Error but was likely given a regular object.`);
            }
            /**
             * Sensitive logs are hidden by default in higher environments.
             */
            if (shouldLogToConsole()) {
                if (Object.keys(additionalDetails).length > 0 && additionalDetails) {
                    console.error(`[${event}]`, error, additionalDetails);
                }
                else {
                    console.error(`[${event}]`, error);
                }
            }
        }
    },
    /**
     * Log some info to the console for lower environments only.
     * Also logs to Lumber if `allowLumber` is true (true by default).
     *
     * @example
     * CustomLogger.info({ event: 'POST_MESSAGE_INIT', info: 'Some info: ' + someString })
     *
     * // To log an object you can just use an array:
     * CustomLogger.info({ event: 'POST_MESSAGE_INIT', info: ['Some info: ', someDataObject] })
     */
    info({ details, event, info, allowLumber = true }) {
        if (!event) {
            console.error('Must provide `event` property to logger.');
            return;
        }
        const additionalDetails = getDetailsAsObject(details);
        if (allowLumber) {
            lumber.info({
                browser: navigator?.userAgent,
                event,
                details: {
                    info,
                    ...additionalDetails
                }
            });
        }
        logToConsole();
        function logToConsole() {
            if (shouldLogToConsole()) {
                if (Object.keys(additionalDetails).length > 0 && additionalDetails) {
                    console.info(`[${event}]`, info, additionalDetails);
                }
                else {
                    console.info(`[${event}]`, info);
                }
            }
        }
    }
};
function shouldLogToConsole() {
    return isLowerEnvironment() || i.getItem(localStorageKey) === 'true';
}
function isLowerEnvironment() {
    const lowerEnvHosts = [
        /(.+\.src\.mastercard\.com)$/i, // Mastercard subdomains
        /(.+\.mcsrcteststore\.com)$/i, // Test store subdomains (Vitrine, etc.),
        /(pcfstage00\.mastercard\.int)$/i, // PCF
        /(localhost)$/i
    ];
    return lowerEnvHosts.some((regex) => !!window.location.hostname.match(regex));
}
function getDetailsAsObject(details) {
    if (!details)
        return {};
    return typeof details === 'object' ? details : { details };
}

let _i18nOptions, _i18nSingleton;
/**
 * Change app-wide locale if supported
 *
 * @param locale - locale in "en-US" or "en" format
 */
function localizeApp(locale) {
    if (!i18nData.isLocaleSupported(locale, false))
        return;
    _i18nSingleton.locale = locale;
    // Set html lang attribute for automation tool purposes
    const html = document.documentElement;
    html.setAttribute('lang', locale);
}
/**
 * Determines a valid user locale from the one given
 *
 * If locale is supported as is, the locale is returned
 * Else, if the country exists, the default language for that country is returned as a locale
 */
function determineLocale(locale) {
    let determinedLocale;
    if (i18nData.isLocaleSupported(locale, false)) {
        determinedLocale = i18nData.formatLocale(locale);
    }
    else if (i18nData.isCountrySupported(locale) || i18nData.isCountrySanctioned(locale)) {
        determinedLocale = _getDefaultLocaleForCountry(locale);
    }
    return determinedLocale || '';
}
/**
 * Get default locale for country
 * Used as last resort when locale cannot be determined by other means (i.e. URL/Browser)
 *
 * if country is live ......... return {defaultLanguageForCountry}-{countryCodeFromLocale}
 * else unsupported country ... return en-{countryCodeFromLocale}
 *
 * @private
 */
function _getDefaultLocaleForCountry(locale) {
    let fallback;
    if (i18nData.isCountryLive(locale)) {
        const languageConfig = i18nData.getCountryConfig(locale);
        fallback = languageConfig?.defaultLanguage;
    }
    else {
        const { countryCode } = i18nData.getLocaleParts(locale);
        fallback = countryCode && `en-${countryCode}`;
    }
    return fallback || '';
}
/**
 * Determines a valid user locale from the browser
 *
 * If the browser's locale is supported and has translations for either the locale or the language, that locale is returned
 * Else if the country of the browser is supported, then the default locale of the country is returned
 */
function determineBrowserLocale() {
    const browserLocales = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
    let locale, defaultLocale;
    for (const browserLocale of browserLocales) {
        if (_isLocaleSupportedAndHasTranslations(browserLocale)) {
            locale = browserLocale;
            break;
        }
        if (!defaultLocale)
            defaultLocale = _getDefaultLocaleForCountry(browserLocale);
    }
    return locale || defaultLocale;
}
/**
 * Determines if the locale is supported by SRC and PM-UI has translations available for this locale
 *
 * @private
 */
function _isLocaleSupportedAndHasTranslations(locale) {
    return i18nData.isLocaleSupported(locale, false) && hasTranslations(locale);
}
/**
 * Returns true if the given locale or the language code has translations
 * For example, if the given locale is fr-CA, if the content has translations
 * for either fr-CA or fr, hasTranslations returns true.
 *
 * @param locale - locale key to check against content keys
 */
function hasTranslations(locale) {
    const languageCode = locale.slice(0, 2);
    return (!!_i18nOptions?.messages &&
        Object.keys(_i18nOptions.messages).some((key) => {
            return locale === key || languageCode === key;
        }));
}
function loadOneTrust({ locale, dataDomainScript, scriptUrl }) {
    if (!locale || !dataDomainScript || !scriptUrl) {
        return CustomLogger.error({
            event: ERRORS.ONE_TRUST_PARAMETER_ERROR,
            error: new Error('Missing required parameters to load OneTrust.')
        });
    }
    if (!i18nData.isLocaleSupported(locale)) {
        return CustomLogger.error({
            event: ERRORS.ONE_TRUST_LOCALE_ERROR,
            error: new Error(`Locale (${locale}) is not supported.`)
        });
    }
    const { languageCode, countryCode } = i18nData.getLocaleParts(locale);
    // Not the first time OneTrust is loading
    if (window.OneTrust) {
        const language = languageCode === 'en' ? 'en' : `${languageCode}-${countryCode}`;
        return window.OneTrust?.changeLanguage?.(language);
    }
    // NOTE: This temporary fix exists to remove multiple div elements generated by OneTrust before reloading the OneTrust script
    // TODO: Remove this temporary fix once this is resolved on the OneTrust end
    document.querySelectorAll('#onetrust-consent-sdk').forEach((e) => e.remove());
    window.OneTrust = {
        geolocationResponse: {
            countryCode
        }
    };
    const loadOptions = {
        attrs: {
            'data-document-language': 'true',
            'data-domain-script': dataDomainScript
        }
    };
    load(scriptUrl, loadOptions, function (error) {
        /**
         * Only catches HTTP errors loading the script (i.e. CORS errors).
         *
         * Apparently there isn't a way to catch when the script itself throws an error.
         */
        if (error) {
            CustomLogger.error({ event: ERRORS.ONE_TRUST_SCRIPT_ERROR, error });
        }
    });
}
function getI18nSingleton() {
    if (!_i18nSingleton) {
        _i18nSingleton = new VueI18n({ ..._i18nOptions });
    }
    return _i18nSingleton;
}
const i18nAdapter = {
    install(Vue, options = {}) {
        _i18nOptions = options;
        Vue.use(VueI18n);
    },
    localizeApp,
    determineLocale,
    determineBrowserLocale,
    hasTranslations,
    getI18nSingleton,
    loadOneTrust
};

function registerValidations(validations) {
    validations.forEach(registerValidation);
    function registerValidation({ name, validator }) {
        Validator.extend(name, validator);
    }
}
const formValidationAdapter = {
    install(Vue, options = {}) {
        Vue.use(VeeValidate, options);
        if (options.validations) {
            registerValidations(options.validations);
        }
        Vue.mixin({
            data() {
                return {
                    /**
                     * Whether or not it has been at least one tick after mounting,
                     * which is when VeeValidate scans for fields to validate in the DOM.
                     */
                    hasScannedFields: false
                };
            },
            computed: {
                /**
                 * Determine if VeeValidate has not validated yet or if there are validation errors
                 * This solves the bug where the submit button flickers from enabled to disabled
                 */
                hasValidationErrors() {
                    const hasFields = this.fields && Object.keys(this.fields).length > 0;
                    /**
                     * If it has passed the point where VeeValidate would scan for fields and there are no fields,
                     * then there is nothing to validate so return false.
                     *
                     * This is useful in scenarios where there is prefilled data to review with an optional toggle to show input fields.
                     * VeeValidate wouldn't find the fields initially so the submit button in those scenarios should be enabled.
                     */
                    if (this.hasScannedFields && !hasFields) {
                        return false;
                    }
                    const hasValidated = Object.values(this.fields).some(({ validated }) => validated);
                    return !hasValidated || this.errors.any();
                }
            },
            async mounted() {
                await this.$nextTick();
                this.hasScannedFields = true;
            }
        });
    }
};

/**
 * Normalize address by joining first & last names into a full name
 *
 * If names are already joined, just remove the split name properties
 */
function joinAddressNames(address) {
    if (!address)
        return;
    const { firstName, lastName, ...joinedAddress } = address || {};
    const fullName = joinedAddress?.name || `${firstName || ''} ${lastName || ''}`;
    joinedAddress.name = fullName.trim().replace(/  +/g, ' ');
    return joinedAddress;
}
/**
 * Normalize address by splitting full name into first and last names
 *
 * If names are already split, just remove the joined name property
 */
function splitAddressNames(address) {
    if (!address)
        return;
    const { name, ...splitAddress } = address || {};
    const [firstName = '', lastName = ''] = name?.split(/\s(.+)/) || [];
    // If missing first or last name, replace them both
    if (!splitAddress.firstName || !splitAddress.lastName) {
        splitAddress.firstName = firstName;
        splitAddress.lastName = lastName;
    }
    return splitAddress;
}

/**
 * @returns the correct masking style for an input
 */
function applyMaskingStyle(input) {
    if (!input)
        return '';
    const maskingStyle = '•';
    const newVal = input.replace(/\*/gi, maskingStyle);
    return newVal;
}

const apiCallRequiresData = ['remember'];
const dataIsRequired = (url) => {
    const lastSegment = url?.split('/').pop();
    return apiCallRequiresData.includes(lastSegment);
};
/**
 * @function addNudetectWidgetHeader
 * @param config - the config object from the original api call
 * @returns the same config object to pass through.
 * @description adds the nudetect widgetData header. Here, dataIsRequired is used to check whether certain api call ;for
 *              eg: /remember(listed in apiCallRequiresData) needs to use getDataAsync() or not. getDataAsync()
 *              fetches nudetect data 20 times at an interval of 100 ms.
 */
async function addNudetectWidgetHeader(config) {
    const dataRequired = dataIsRequired(config.url);
    const widgetData = dataRequired ? await nuDetect.getDataAsync() : nuDetect.getData();
    if (widgetData) {
        if (!config.headers) {
            config.headers = {};
        }
        config.headers['X-SRC-NUDETECT-WIDGETDATA'] = widgetData;
    }
    return config;
}

const queue = (() => {
    const requestQueue = [];
    /**
     * Stores the relevant information of the parent app
     */
    const store = {
        apiEndpoint: '',
        appVersion: '',
        source: ''
    };
    /**
     * Initialize the queue store values
     * @function initLogger
     * @param apiEndpoint - the logging endpoint
     * @param appVersion - version of the parent app
     * @param source - name of the project
     */
    const initLogger = (options) => {
        const { apiEndpoint, appVersion, source } = options;
        store.apiEndpoint = apiEndpoint;
        store.appVersion = appVersion;
        store.source = source;
    };
    /**
     * @function add
     * @param api - the endpoint called to track
     * @param start - the time the request was initiated
     * @param method - the request method
     * @description adds the request to the queue array to be upon completion
     */
    const add = (api, start, method) => requestQueue.push({ api, start, method });
    /**
     * @function finish
     * @param api - the endpoint called to track
     * @param end - the time the endpoint responded
     * @param status - the response status code
     * @description initiates sendLog function if not a logging service api call
     */
    const finish = (api, end, status) => {
        if (!api.includes(store.apiEndpoint)) {
            sendLog(api, end, status);
        }
    };
    /**
     * @function getFormattedLogs
     * @param api - the endpoint called to track
     * @param end - the time the endpoint responded
     * @param status - the response status code
     * @returns {Array} the formatted log to be sent to the logging service
     */
    const getFormattedLogs = (api, end, status) => {
        return requestQueue
            .filter((request) => api.includes(request.api))
            .map(({ start, api, method }) => {
            return {
                event: 'PERF_API_RESPONSE',
                details: {
                    method,
                    endpoint: api,
                    status,
                    duration: end - start
                }
            };
        });
    };
    /**
     * @function sendLog
     * @param api - the endpoint called to track
     * @param end - the time the endpoint responded
     * @param status - the response status code
     * @description Builds the logging data object and calls function to format
     * logs. Sends the info to the logging service endpoint
     * NOTE: Eventually will be postMessage into a webworker
     */
    const sendLog = (api, end, status) => {
        const logs = getFormattedLogs(api, end, status);
        if (logs.length) {
            lumber.info(logs);
        }
        requestQueue.length = 0;
    };
    return {
        add,
        finish,
        initLogger
    };
})();

/**
 * @function initLogger
 * @param apiEndpoint - the logging endpoint
 * @param appVersion - version of the parent app
 * @param source - name of the project
 */
const logXhrInit = (options) => {
    queue.initLogger(options);
};
/**
 * @function logXhrStart
 * @param config - the config object from the original api call
 * @returns the same config object to pass through.
 * @description calls the queue.add method when an api call is made
 */
const logXhrStart = (config) => {
    queue.add(config.url, Date.now(), config.method);
    return config;
};
/**
 * @function logXhrFinish
 * @param response - the response from the original api call
 * @returns the same response object to pass through.
 * @description calls the queue.finish method upon api response
 */
const logXhrFinish = (response) => {
    queue.finish(response.config.url, Date.now(), response.status);
    return response;
};
/**
 * @function logXhrReject
 * @returns the error object in a promise reject method
 * @description calls the queue.finish method upon an api error response
 */
const logXhrReject = (error) => {
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        queue.finish(error.response.config.url, Date.now(), error.response?.status);
    }
    else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser
        queue.finish(error.request.url, Date.now(), error.request?.status);
    }
    else {
        // Something happened in setting up the request that triggered an Error
        queue.finish('error', Date.now(), error.request?.status);
    }
    return Promise.reject(error);
};

async function getWebAuthNSupport() {
    let isPlatformAuth = false;
    let isWebAuthNSupported = false;
    let isSPCSupported = false;
    let isAuthScriptSuccessful = true;
    try {
        if (window.PublicKeyCredential) {
            isWebAuthNSupported = true;
            isPlatformAuth =
                await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        }
        isSPCSupported = await isSecurePaymentConfirmationSupported();
    }
    catch (err) {
        isAuthScriptSuccessful = false;
    }
    return {
        isPlatformAuth,
        isWebAuthNSupported,
        isSPCSupported,
        isAuthScriptSuccessful
    };
}
/**
 * NOTE: Dummy data is needed for required properties. Log the errors in the `catch` blocks to see which are necessary.
 *
 * {@link https://developer.chrome.com/articles/authenticate-secure-payment-confirmation/#feature-detection}
 */
async function isSecurePaymentConfirmationSupported() {
    if (!('PaymentRequest' in window)) {
        return false;
    }
    try {
        // eslint-disable-next-line
        const supportedInstruments = [
            {
                supportedMethods: 'secure-payment-confirmation',
                data: {
                    rpId: 'rp.example',
                    credentialIds: [new Uint8Array(1)],
                    challenge: new Uint8Array(1),
                    instrument: {
                        displayName: ' ',
                        icon: 'https://src.mastercard.com/profile/favicon.ico'
                    },
                    payeeOrigin: 'https://mastercard.com'
                }
            }
        ];
        // eslint-disable-next-line
        const details = {
            total: {
                label: 'Total',
                amount: {
                    currency: 'USD',
                    value: '0'
                }
            }
        };
        const request = new PaymentRequest(supportedInstruments, details);
        return request.canMakePayment();
    }
    catch (_) {
        throw new Error('Error in isSecurePaymentConfirmationSupported');
    }
}

export { CustomLogger, addNudetectWidgetHeader, applyMaskingStyle, formValidationAdapter, getWebAuthNSupport, i18nAdapter, isSecurePaymentConfirmationSupported, joinAddressNames, loadOneTrust, logXhrFinish, logXhrInit, logXhrReject, logXhrStart, splitAddressNames };
//# sourceMappingURL=index.esm.js.map
